import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, TextField } from '@mui/material';
import debounce from 'lodash/debounce';

import withFormControl from '@/hoc/withFormControl';
import { useDirection } from '@/hooks/useDirection';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

interface NumericRangeSelectProps {
  id?: string;
  name: string;
  value: { min: number; max: number };
  onChange?: (range: { min?: number; max?: number }) => void;
  options: string[];
  min?: number;
  max?: number;
}

const prepareOuterValue = ({ min, max }: { min: number | null; max: number | null }) => ({
  min: !isNaN(min!) ? min : null,
  max: !isNaN(max!) ? max : null
});

const NumericRangeField: FC<NumericRangeSelectProps> = ({ id, value, onChange, min, max }) => {
  const { t } = useTranslation('common');
  const { applyDirectionalStyles } = useDirection();
  const [innerValue, setInnerValue] = useState({
    min: value?.min?.toString() || '',
    max: value?.max?.toString() || ''
  });
  const [errors, setErrors] = useState<{ min?: boolean; max?: boolean }>({});
  const numericInnerValue = useMemo(
    () => ({
      min: parseFloat(innerValue.min),
      max: parseFloat(innerValue.max)
    }),
    [innerValue]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce(newValue => onChange?.(newValue), 300),
    [onChange]
  );

  const handleMinChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = event.target.value;
      const numericNewMin = parseFloat(newMin);

      setInnerValue(prev => ({ ...prev, min: newMin }));
      if (newMin === '') {
        debouncedOnChange(prepareOuterValue({ ...numericInnerValue, min: null }));
      } else if (
        !isNaN(numericNewMin) &&
        (isNaN(numericInnerValue.max) || numericNewMin <= numericInnerValue.max) &&
        numericNewMin >= (min ?? -Infinity)
      ) {
        setErrors(prev => ({ ...prev, min: false }));
        debouncedOnChange(prepareOuterValue({ ...numericInnerValue, min: numericNewMin }));
      } else {
        setErrors(prev => ({ ...prev, min: true }));
      }
    },
    [numericInnerValue, min, debouncedOnChange]
  );

  const handleMaxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = event.target.value;
      const numericNewMax = parseFloat(newMax);
      setInnerValue(prev => ({ ...prev, max: newMax }));
      if (newMax === '') {
        debouncedOnChange({ ...numericInnerValue, max: null });
      } else if (
        !isNaN(numericNewMax) &&
        (isNaN(numericInnerValue.min) || numericNewMax >= numericInnerValue.min) &&
        numericNewMax <= (max ?? Infinity)
      ) {
        setErrors(prev => ({ ...prev, max: false }));
        debouncedOnChange({ ...numericInnerValue, max: numericNewMax });
      } else {
        setErrors(prev => ({ ...prev, max: true }));
      }
    },
    [numericInnerValue, max, debouncedOnChange]
  );

  const handleMinBlur = useCallback(() => {
    if (
      isNaN(numericInnerValue.min) ||
      numericInnerValue.min > numericInnerValue.max ||
      numericInnerValue.min < (min ?? -Infinity)
    ) {
      const correctedMin = Math.max(
        min ?? -Infinity,
        Math.min(numericInnerValue.min, numericInnerValue.max, max ?? Infinity)
      );
      setInnerValue(prev => ({ ...prev, min: correctedMin?.toString() }));
      setErrors(prev => ({ ...prev, min: false }));
      debouncedOnChange(prepareOuterValue({ ...numericInnerValue, min: correctedMin }));
    }
  }, [numericInnerValue, min, max, debouncedOnChange]);

  const handleMaxBlur = useCallback(() => {
    if (
      isNaN(numericInnerValue.max) ||
      numericInnerValue.max < numericInnerValue.min ||
      numericInnerValue.max > (max ?? Infinity)
    ) {
      const correctedMax = Math.min(
        max ?? Infinity,
        Math.max(numericInnerValue.min, numericInnerValue.max, min ?? -Infinity)
      );
      setInnerValue(prev => ({ ...prev, max: correctedMax?.toString() }));
      setErrors(prev => ({ ...prev, max: false }));
      debouncedOnChange(prepareOuterValue({ ...numericInnerValue, max: correctedMax }));
    }
  }, [numericInnerValue, max, min, debouncedOnChange]);

  useEffect(() => {
    const { min: innerValueMin, max: innerValueMax } = innerValue;
    const newValueMin = value?.min?.toString() || '';
    const newValueMax = value?.max?.toString() || '';
    if (newValueMin !== innerValueMin || newValueMax !== innerValueMax) return;

    setInnerValue({
      min: value?.min?.toString() || '',
      max: value?.max?.toString() || ''
    });
  }, [value?.min, value?.max]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack
      direction="row"
      gap={1}
      sx={{
        '& .MuiTextField-root .MuiInputBase-root': {
          pt: 0.5,
          pb: 0.5,
          ...applyDirectionalStyles(1, 0, 'pl', 'pr'),
          bgcolor: 'background.default'
        },
        '& .MuiTextField-root .MuiInputBase-root .MuiInputBase-input::placeholder': {
          ...typography.body2,
          color: 'grey.700'
        },
        '& .MuiTextField-root .MuiInputBase-root fieldset': {
          borderColor: 'grey.600'
        },
        '& .MuiFormControl-root': {
          padding: 0
        },
        '& .MuiTextField-root .MuiOutlinedInput-root.Mui-error fieldset': {
          border: borders.default,
          borderColor: 'error.main'
        }
      }}
    >
      <TextField
        InputProps={{ id, type: 'number' }}
        value={innerValue.min}
        onChange={handleMinChange}
        onBlur={handleMinBlur}
        placeholder={t('components.numericRange.placeholderMin')}
        autoComplete="off"
        inputProps={{ inputMode: 'numeric', min }}
        error={errors.min}
      />
      <TextField
        InputProps={{ id: `${id}-max`, type: 'number' }}
        value={innerValue.max}
        onChange={handleMaxChange}
        onBlur={handleMaxBlur}
        placeholder={t('components.numericRange.placeholderMax')}
        autoComplete="off"
        inputProps={{ inputMode: 'numeric', max }}
        error={errors.max}
      />
    </Stack>
  );
};

const NumericRange = withFormControl<
  { min: number; max: number },
  { min: number; max: number },
  NumericRangeSelectProps
>(NumericRangeField);

export default NumericRange;
